/**
 * All dashboard time display is in Europe/Brussels (auto DST — the IANA zone, never
 * a fixed offset). ClickHouse returns UTC timestamps as "YYYY-MM-DD HH:mm:ss[.SSS]"
 * with NO zone marker; parsed naïvely they'd be read as local time (V8), so we force
 * the UTC instant first, then format in the Brussels zone.
 */

export const TZ = 'Europe/Brussels';

/** Parse a ClickHouse UTC timestamp (or an ISO string) into the instant it denotes. */
export function parseUtc(s: string): Date {
  if (!s) return new Date(Number.NaN);
  const iso = s.includes('T') ? s : s.replace(' ', 'T');
  const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
}

const fmt = (opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, ...opts });

const F_DATETIME = fmt({ dateStyle: 'short', timeStyle: 'short' });
const F_DATETIME_FULL = fmt({ dateStyle: 'medium', timeStyle: 'short' });
const F_DATE = fmt({ dateStyle: 'short' });
const F_COMPACT = fmt({ day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const F_HM = fmt({ hour: '2-digit', minute: '2-digit' });
const F_CSV = fmt({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** Short date + time, e.g. "21/07/2026 12:23" (Brussels). */
export const fmtDateTime = (s: string): string => F_DATETIME.format(parseUtc(s));
/** Medium date + time, for hover/detail. */
export const fmtDateTimeFull = (s: string): string => F_DATETIME_FULL.format(parseUtc(s));
/** Date only, e.g. "21/07/2026". */
export const fmtDate = (s: string): string => F_DATE.format(parseUtc(s));
/** Dense "dd/mm HH:MM" for tables. */
export const fmtCompact = (s: string): string => F_COMPACT.format(parseUtc(s));
/** Hour:minute only, for hourly-chart labels and live feeds. */
export const fmtHM = (s: string): string => F_HM.format(parseUtc(s));
/** Sortable-ish CSV stamp "21/07/2026 12:23:45" (Brussels, no comma). */
export const fmtCsvStamp = (s: string): string => F_CSV.format(parseUtc(s)).replace(',', '');
